import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { currentUserKey } from 'hooks/use-current-user';
import { useIsMobile } from 'hooks/use-mobile';
import {
  useStartWhatsAppLink,
  useUnlinkWhatsApp,
  useWhatsAppLink,
  whatsAppChatUrl,
} from 'hooks/use-whatsapp-link';
import { ExternalLink, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';

interface WhatsAppSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** "19174958599" → "+1 917 495 8599" is not derivable without a region table; show E.164. */
const formatNumber = (digits: string | null) => (digits ? `+${digits}` : '');

function OpenWhatsAppButton ({ href, variant = 'default' }: { href: string; variant?: 'default' | 'outline' }) {
  return (
    <Button asChild variant={variant}>
      <a href={href} target="_blank" rel="noopener noreferrer">
        <ExternalLink className="size-4" />
        Open WhatsApp
      </a>
    </Button>
  );
}

export function WhatsAppSettingsDialog ({ open, onOpenChange }: WhatsAppSettingsDialogProps) {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { data, isLoading, isError } = useWhatsAppLink({ enabled: open, pollWhilePending: true });
  const startLink = useStartWhatsAppLink();
  const unlink = useUnlinkWhatsApp();

  // Once the phone-side code lands, the poll flips `linked`; tell the user
  // and refresh the cached user so the menu label updates.
  const wasLinked = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (data === undefined) return;
    if (wasLinked.current === false && data.linked) {
      toast.success(`WhatsApp linked to ${formatNumber(data.phone_number)}`);
      queryClient.invalidateQueries({ queryKey: currentUserKey });
    }
    wasLinked.current = data.linked;
  }, [data, queryClient]);

  const handleStart = async () => {
    try {
      await startLink.mutateAsync();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleUnlink = async () => {
    try {
      await unlink.mutateAsync();
      toast.success('WhatsApp unlinked');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const pending = !!data && !data.linked && !!data.link_code;
  // Pre-fills the link code while one is pending; a plain chat link otherwise.
  const chatUrl = data ? whatsAppChatUrl(data.bot_phone_digits, pending ? data.link_code : null) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>WhatsApp</DialogTitle>
          <DialogDescription>
            Chat with the HiLo agent from your phone. Messages you send to{' '}
            <span className="font-mono">{data?.bot_phone_number ?? '…'}</span> are answered in
            a chat on your account.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {isError && <div className="text-sm text-destructive">Failed to load WhatsApp status.</div>}

        {data?.linked && chatUrl && (
          <div className="flex flex-col gap-3 text-sm">
            <div>
              Linked number: <span className="font-mono">{formatNumber(data.phone_number)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              A conversation closes after an hour of quiet. Send <code>/new</code> to start a
              fresh one at any time and <code>/continue</code> to pick up a closed one.
            </p>
            <div>
              <OpenWhatsAppButton href={chatUrl} variant="outline" />
            </div>
          </div>
        )}

        {data && !data.linked && !pending && (
          <p className="text-sm">
            Get a one-time code, then send it to the number above from the phone you want to
            link. The code expires after 15 minutes.
          </p>
        )}

        {pending && chatUrl && (
          <div className="flex flex-col gap-3 text-sm">
            {/* A QR is only useful when the reader is on a different device
                than the phone, so the mobile layout goes straight to the button. */}
            {!isMobile && (
              <div className="flex flex-col items-center gap-2">
                {/* marginSize is the QR quiet zone: a white border the camera
                    needs to find the code's edges on a dark page. Fixed
                    black-on-white colours for the same reason. */}
                <div className="rounded-md bg-white p-1" data-testid="whatsapp-qr">
                  <QRCodeSVG value={chatUrl} size={160} marginSize={2} bgColor="#ffffff" fgColor="#000000" />
                </div>
                <div className="text-xs text-muted-foreground text-center">
                  Scan with your phone camera to open WhatsApp with the code filled in.
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <OpenWhatsAppButton href={chatUrl} />
            </div>
            <div className="text-xs text-muted-foreground">
              Or send this code to <span className="font-mono">{data.bot_phone_number}</span> on
              WhatsApp yourself:
            </div>
            <div className="font-mono text-3xl tracking-[0.3em] text-center py-2 rounded-md border">
              {data.link_code}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Waiting for your message…
            </div>
          </div>
        )}

        <DialogFooter>
          {data?.linked ? (
            <Button variant="outline" onClick={handleUnlink} disabled={unlink.isPending}>
              {unlink.isPending ? 'Unlinking…' : 'Unlink'}
            </Button>
          ) : (
            <Button onClick={handleStart} disabled={startLink.isPending || isLoading}>
              {startLink.isPending ? 'Working…' : pending ? 'New code' : 'Get link code'}
            </Button>
          )}
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
